import {
    faLocationDot
} from '@fortawesome/pro-regular-svg-icons/faLocationDot';
import {
    faSearch
} from '@fortawesome/pro-regular-svg-icons/faSearch';
import {
    faSliders
} from '@fortawesome/pro-regular-svg-icons/faSliders';
import {
    faXmark
} from '@fortawesome/pro-solid-svg-icons/faXmark';
import {
    FontAwesomeIcon
} from '@fortawesome/react-fontawesome';
import {
    Autocomplete,
    type AutocompleteInputChangeReason,
    Box,
    Button,
    CircularProgress,
    FormControl,
    Grid2 as Grid,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    type SxProps,
    TextField,
    type Theme,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    useQuery
} from '@tanstack/react-query';
import {
    type ChangeEvent,
    type FC,
    type FocusEvent,
    type FormEvent,
    type ReactNode,
    type SyntheticEvent,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react';
import {
    createSearchParams,
    useNavigate
} from 'react-router-dom';
import {
    toast
} from 'react-toastify';

import {
    NEAR_MY_LOCATION_LABEL,
    useLocationSearch
} from '@views/LandingPage/useLocationSearch';

import TypedButton from '@components/typed/Button';
import Typography from '@components/typed/Typography';

import {
    getSICCodeCategories
} from '@services/search';

import {
    MS_24_HOURS
} from '@constants/site';
import {
    POL_THEME,
    ThemeContext
} from '@constants/theme';

import type {
    SICCodeCategory
} from '@types';

import type {
    SearchFormProps
} from './types';

import styles from './styles';

const SearchForm: FC<SearchFormProps> = (props) => {
    const {
        isSupporterSearch = false,
        searchLocationValue,
        searchNameValue,
        selectedSICCategory,
        setIsSearchDrawerOpen = () => {},
        setSearchLocationValue,
        setSearchNameValue,
        setSelectedSICCategory
    } = props;

    const themeContext = useContext(ThemeContext);

    const navigate = useNavigate();
    const {
        getLocationQueryFields,
        handleOptionSelect,
        isLoading,
        isUsingCurrentLocation,
        optionsList,
        setInputText
    } = useLocationSearch();

    const muiTheme = useTheme();
    const isMdDown = useMediaQuery(
        muiTheme.breakpoints.down('md')
    );

    const isPOL = themeContext === POL_THEME;
    const searchLocationPlaceholder = 'City, State, or Zip';

    const [
        isDisplayingLocationOptions,
        setIsDisplayingLocationOptions
    ] = useState<boolean>(false);
    const [
        isFiltersModalOpen,
        setIsFiltersModalOpen
    ] = useState<boolean>(false);

    const [
        locationError,
        setLocationError
    ] = useState<string>('');

    const inputRefDesktop = useRef<HTMLInputElement | null>(null);
    const inputRefMobile = useRef<HTMLInputElement | null>(null);

    // ---
    // TanStack Query bindings
    // ---

    const {
        data: SICCategories
    } = useQuery({
        queryFn: getSICCodeCategories,
        queryKey: [
            'sic-categories'
        ],
        staleTime: MS_24_HOURS
    });

    const validateLocation = (value: string): boolean => {
        const trimmed = value.trim();

        if (!trimmed) {
            setLocationError('Please enter a location');

            return false;
        }

        setLocationError('');

        return true;
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();

        setIsDisplayingLocationOptions(false);

        if (!validateLocation(searchLocationValue)) {
            requestAnimationFrame(() => {
                (isMdDown ? inputRefMobile : inputRefDesktop).current?.focus();
            });

            return;
        }

        setIsSearchDrawerOpen(false);

        try {
            const searchLocationFields = await getLocationQueryFields();

            const searchCategory = (): string => {
                switch (true) {
                    case isSupporterSearch && !!selectedSICCategory:
                        return selectedSICCategory.toString();

                    case isSupporterSearch:
                        return '';

                    case !isPOL:
                        return 'Community';

                    default:
                        return 'Catholic';
                }
            };

            const search = createSearchParams({
                ...searchLocationFields,
                searchCategory: searchCategory(),
                searchName: searchNameValue,
                searchType: isSupporterSearch ? 'supporter' : 'organization',
                timestamp: (Math.floor(Date.now() / 1000)).toString()
            }).toString();

            navigate({
                pathname: '/search',
                search
            });
        } catch {
            toast.error('Encountered error during search, please try again.');
        }
    };

    const handleLocationInputChange = (_event: SyntheticEvent<Element, Event>, option: string | null): void => {
        setIsDisplayingLocationOptions(false);

        const next = option ?? '';

        setSearchLocationValue(next);
        handleOptionSelect(next);

        if (locationError && next.length) { setLocationError(''); }
    };

    const handleLocationInputTextChange = (_event: SyntheticEvent, value: string, reason: AutocompleteInputChangeReason): void => {
        if (reason !== 'input' && reason !== 'clear') { return; }

        if (reason === 'input') { setIsDisplayingLocationOptions(true); }
        if (reason === 'clear') { setIsDisplayingLocationOptions(false); }

        setSearchLocationValue(value);
        setInputText(value);

        if (locationError && value.length) { setLocationError(''); }
    };

    const handleLocationInputClear = (): void => setIsDisplayingLocationOptions(false);

    const handleLocationBlur = (e: FocusEvent<HTMLInputElement>): void => {
        setIsDisplayingLocationOptions(false);

        if (locationError && e.currentTarget.value.length) { setLocationError(''); }
    };

    const searchNamePlaceholder = (): string => {
        if (isSupporterSearch) {
            return 'Business Name';
        }

        if (!isPOL) {
            return 'Organization Name';
        }

        return 'Church Name';
    };

    useEffect(
        (): void => {
            setInputText(searchLocationValue);

            if (searchLocationValue === NEAR_MY_LOCATION_LABEL) {
                handleOptionSelect(NEAR_MY_LOCATION_LABEL);
            }
        },
        [
            handleOptionSelect,
            searchLocationValue,
            setInputText
        ]
    );

    const searchNameInput = (sxStyles?: SxProps<Theme>): ReactNode => (
        <TextField
            label={(
                <Typography variant={'body1'}>
                    {searchNamePlaceholder()}
                </Typography>
            )}
            onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                setSearchNameValue(event.target.value);
            }}
            sx={sxStyles}
            value={searchNameValue}
            variant={'standard'}
        />
    );

    const searchLocationInput = (inputSXStyles?: SxProps<Theme>): ReactNode => (
        <Autocomplete
            filterOptions={(opts) => opts}
            freeSolo
            inputValue={searchLocationValue}
            loading={isLoading}
            onChange={handleLocationInputChange}
            onClose={handleLocationInputClear}
            onInputChange={handleLocationInputTextChange}
            onOpen={() => setIsDisplayingLocationOptions(true)}
            open={isDisplayingLocationOptions}
            openOnFocus
            options={optionsList}
            renderInput={(params) => (
                <TextField
                    {...params}
                    error={!!locationError}
                    inputRef={isMdDown ? inputRefMobile : inputRefDesktop}
                    label={(
                        <Typography variant={'body1'}>
                            {locationError || searchLocationPlaceholder}
                        </Typography>
                    )}
                    required
                    slotProps={{
                        htmlInput: {
                            ...params.inputProps,
                            onBlur: handleLocationBlur
                        },
                        input: {
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {isLoading ? (
                                        <CircularProgress
                                            aria-hidden
                                            size={20}
                                        />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                            startAdornment: isUsingCurrentLocation ? (
                                <Box
                                    aria-hidden
                                    component={FontAwesomeIcon}
                                    icon={faLocationDot}
                                    size={'2x'}
                                    sx={[
                                        styles.locationDot,
                                        styles.locationDotInput
                                    ]}
                                />
                            ) : null
                        }
                    }}
                    sx={inputSXStyles}
                    variant={'standard'}
                />
            )}
            renderOption={(inputProps, option) => {
                const {
                    key,
                    ...rest
                } = inputProps;
                const isNearMe = option === NEAR_MY_LOCATION_LABEL;

                return (
                    <Box
                        component={'li'}
                        key={key}
                        sx={styles.option}
                        {...rest}
                    >
                        <Typography variant={isNearMe ? 'h5' : 'h6'}>
                            {isNearMe ? (
                                <>
                                    <Box
                                        aria-hidden
                                        component={FontAwesomeIcon}
                                        icon={faLocationDot}
                                        sx={styles.locationDot}
                                    />
                                        &nbsp;
                                </>
                            ) : null}
                            {option}
                        </Typography>
                    </Box>
                );
            }}
            sx={styles.locationInput}
            // MUI Autocomplete can show the clear icon when `value` is non-null.
            // Coerce empty string to `null` so a blank input is treated as "no value".
            value={searchLocationValue || null}
        />
    );

    const searchFilterButtonCombined = (
        <Button
            aria-label={`Filter${selectedSICCategory ? ' (1 selected)' : ''}`}
            onClick={() => setIsFiltersModalOpen(true)}
            sx={[
                styles.roundButton,
                styles.roundFilterButton
            ]}
            title={'Filter'}
            type={'button'}
        >
            <FontAwesomeIcon
                aria-hidden
                icon={faSliders}
                size={'xl'}
            />
            {selectedSICCategory && (
                <Box sx={styles.roundFilterButtonCount}>
                    {'1'}
                </Box>
            )}
        </Button>
    );

    const categorySelectOptions = SICCategories?.map((details) => (
        <MenuItem
            key={details.id}
            value={details.id}
        >
            {details.name}
        </MenuItem>
    ));

    const combinedSearchForm = (
        <Box sx={styles.combinedFormContainer}>
            {isSupporterSearch && searchFilterButtonCombined}
            {searchNameInput([
                styles.formInput,
                styles.inputLeft,
                !isSupporterSearch && styles.inputMarginLeft
            ])}
            <Box sx={styles.divider} />
            {searchLocationInput([
                styles.formInput,
                styles.inputRight,
                !!locationError && styles.formInputError
            ])}
            <Button
                aria-label={'Search'}
                sx={[
                    styles.roundButton,
                    styles.roundSearchButton
                ]}
                title={'Search'}
                type={'submit'}
            >
                <FontAwesomeIcon
                    aria-hidden
                    icon={faSearch}
                    size={'2x'}
                />
            </Button>
        </Box>
    );

    const splitSearchForm = (
        <>
            <Box
                sx={[
                    styles.formInput,
                    styles.splitInput
                ]}
            >
                {searchNameInput()}
            </Box>
            <Box
                sx={[
                    styles.formInput,
                    styles.splitInput,
                    !!locationError && styles.formInputError
                ]}
            >
                {searchLocationInput()}
            </Box>
            <Grid
                container
                spacing={1}
                sx={styles.mobileButtonContainer}
            >
                {isSupporterSearch && (
                    <TypedButton
                        onClick={() => setIsFiltersModalOpen(true)}
                        sx={styles.mobileFilterButton}
                    >
                        <FontAwesomeIcon icon={faSliders} />
                        <Box
                            component={'span'}
                            sx={styles.innerButtonSpan}
                        >
                            {`Filter${selectedSICCategory ? ' (1)' : ''}`}
                        </Box>
                    </TypedButton>
                )}

                <TypedButton
                    sx={styles.mobileSearchButton}
                    type={'submit'}
                >
                    <FontAwesomeIcon
                        icon={faSearch}
                    />
                    <Box
                        component={'span'}
                        sx={styles.innerButtonSpan}
                    >
                        {'Search'}
                    </Box>
                </TypedButton>
            </Grid>
        </>
    );

    return (
        <>
            <Box
                component={'form'}
                id={'primary-search-form'}
                noValidate
                onSubmit={handleSubmit}
            >
                {isMdDown ? splitSearchForm : combinedSearchForm}
            </Box>

            {isSupporterSearch && (
                <Modal
                    onClose={() => setIsFiltersModalOpen(false)}
                    open={isFiltersModalOpen}
                    sx={styles.overlay}
                >
                    <Box
                        aria-labelledby={'filter-title'}
                        aria-modal
                        role={'dialog'}
                        sx={styles.modal}
                    >
                        <Button
                            aria-label={'Close filters'}
                            onClick={() => setIsFiltersModalOpen(false)}
                            sx={styles.modalClose}
                            variant={'text'}
                        >
                            <FontAwesomeIcon
                                color={muiTheme.colors.doveGray}
                                icon={faXmark}
                                size={'2x'}
                            />
                        </Button>
                        <Typography
                            id={'filter-title'}
                            variant={'body1'}
                        >
                            {'Filter By Business Type:'}
                        </Typography>
                        <FormControl
                            fullWidth
                            sx={styles.formSelect}
                        >
                            <InputLabel id={'category-select-label'}>
                                {'Select a Category'}
                            </InputLabel>
                            <Select
                                id={'category-select'}
                                label={'Select a Category'}
                                labelId={'category-select-label'}
                                MenuProps={{
                                    MenuListProps: {
                                        sx: styles.formSelectList
                                    },
                                    PaperProps: {
                                        sx: styles.formSelectPaper
                                    }
                                }}
                                onChange={(event) => setSelectedSICCategory(event.target.value as SICCodeCategory['id'])}
                                value={selectedSICCategory}
                            >
                                <MenuItem value={''}>
                                    {'Select a Category'}
                                </MenuItem>
                                {categorySelectOptions}
                            </Select>
                        </FormControl>
                    </Box>
                </Modal>
            )}
        </>
    );
};

export default SearchForm;
